<!-- app/javascript/dashboard/routes/dashboard/clusters/ClusterMemberRow.vue -->
<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  membership: { type: Object, required: true },
  clusterId: { type: [String, Number], required: true },
  roles: { type: Array, default: () => [] },
});

const store = useStore();
const { t } = useI18n();

const member = computed(() => props.membership.member);
const isSuggested = computed(() => props.membership.status === 'suggested');

const runOrAlert = async fn => {
  try {
    await fn();
  } catch (e) {
    useAlert(t('CLUSTERS.API.ERROR'));
  }
};

const setRole = role =>
  runOrAlert(() =>
    store.dispatch('clusterMemberships/update', {
      clusterId: props.clusterId,
      id: props.membership.id,
      membership: { role },
    })
  );

const setStatus = status =>
  runOrAlert(() =>
    store.dispatch('clusterMemberships/update', {
      clusterId: props.clusterId,
      id: props.membership.id,
      membership: { status },
    })
  );

const makeHead = () =>
  runOrAlert(() =>
    store.dispatch('clusterMemberships/update', {
      clusterId: props.clusterId,
      id: props.membership.id,
      membership: { is_primary: true },
    })
  );

const remove = () =>
  runOrAlert(() =>
    store.dispatch('clusterMemberships/delete', {
      clusterId: props.clusterId,
      id: props.membership.id,
    })
  );
</script>

<template>
  <div class="flex items-center gap-3 py-2 border-b border-n-weak">
    <span
      v-if="membership.is_primary"
      class="i-lucide-star text-amber-500 size-4 shrink-0"
      :title="t('CLUSTERS.MEMBERS.HEAD')"
    />
    <span v-else class="size-4 shrink-0" />

    <span class="flex-1 truncate text-sm text-n-slate-12">
      {{ member?.name }}
      <span class="text-n-slate-10 text-xs ml-1">({{ member?.type }})</span>
    </span>

    <select
      :value="membership.role || ''"
      class="text-sm h-7 px-2 border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
      @change="setRole($event.target.value || null)"
    >
      <option value="">—</option>
      <option v-for="r in roles" :key="r" :value="r">{{ r }}</option>
    </select>

    <span
      class="text-xs px-2 py-0.5 rounded-full shrink-0"
      :class="
        isSuggested
          ? 'bg-amber-100 text-amber-800'
          : membership.status === 'confirmed'
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-ruby-3 text-n-ruby-11'
      "
    >
      {{ t(`CLUSTERS.STATUS.${membership.status.toUpperCase()}`) }}
    </span>

    <template v-if="isSuggested">
      <button
        type="button"
        class="text-green-600 text-xs hover:underline shrink-0"
        @click="setStatus('confirmed')"
      >
        {{ t('CLUSTERS.MEMBERS.CONFIRM') }}
      </button>
      <button
        type="button"
        class="text-red-600 text-xs hover:underline shrink-0"
        @click="setStatus('rejected')"
      >
        {{ t('CLUSTERS.MEMBERS.REJECT') }}
      </button>
    </template>

    <button
      v-if="!membership.is_primary"
      type="button"
      class="text-xs text-n-slate-10 hover:text-n-slate-12 hover:underline shrink-0"
      @click="makeHead"
    >
      {{ t('CLUSTERS.MEMBERS.MAKE_HEAD') }}
    </button>

    <button
      type="button"
      class="i-lucide-trash-2 size-4 text-red-500 hover:text-red-700 shrink-0"
      :title="t('CLUSTERS.MEMBERS.REMOVE')"
      @click="remove"
    />
  </div>
</template>
